import React from 'react';
import {
    UserOutlined,
    ProductOutlined,
    MoneyCollectOutlined,
    OrderedListOutlined,
    SettingOutlined,
    GiftOutlined,
} from '@ant-design/icons';

/** Which live count, if any, renders as a badge on this item. */
export type BadgeSource = 'withdrawals' | 'orders';

export interface NavLeaf {
    key: string;
    label: string;
    path: string;
    icon?: React.ReactNode;
    badge?: BadgeSource;
}

export interface NavGroup {
    key: string;
    label: string;
    icon: React.ReactNode;
    children: NavLeaf[];
}

export interface NavSection {
    heading: string;
    items: (NavGroup | NavLeaf)[];
}

export const isGroup = (item: NavGroup | NavLeaf): item is NavGroup =>
    (item as NavGroup).children !== undefined;

export const navSections: NavSection[] = [
    {
        heading: '管理',
        items: [
            {
                key: 'members',
                label: '会员',
                icon: <UserOutlined />,
                children: [
                    { key: 'user-list', label: '会员列表', path: '/user' },
                    { key: 'user-new', label: '新建会员', path: '/newuser' },
                ],
            },
            /*
            {
                key: 'lucky-spin',
                label: '幸运转盘',
                path: '/lucky-spin',
                icon: <GiftOutlined />,
            },
            */
            {
                key: 'products',
                label: '商品',
                icon: <ProductOutlined />,
                children: [
                    { key: 'product-list', label: '商品列表', path: '/product' },
                    { key: 'product-new', label: '新建商品', path: '/newproduct' },
                    { key: 'product-sets', label: '配套管理', path: '/product-sets' },
                ],
            },
        ],
    },
    {
        heading: '财务',
        items: [
            {
                key: 'withdrawal',
                label: '提款',
                path: '/withdrawal',
                icon: <MoneyCollectOutlined />,
                badge: 'withdrawals',
            },
            {
                key: 'orders',
                label: '订单',
                path: '/orders',
                icon: <OrderedListOutlined />,
                badge: 'orders',
            },
        ],
    },
    {
        heading: '系统',
        items: [
            {
                key: 'settings',
                label: '系统设置',
                path: '/settings',
                icon: <SettingOutlined />,
            },
            {
                key: 'administrator',
                label: '管理员',
                path: '/administrator',
                icon: <UserOutlined />,
            },
        ],
    },
];

/**
 * Resolves a route to the nav leaf that should look active.
 * Detail routes fold into their list item — /user/7 highlights 会员列表, not nothing.
 */
export function activeLeafFor(pathname: string): string {
    const p = pathname.toLowerCase().replace(/\/+$/, '') || '/';

    if (p === '/newuser') return 'user-new';
    if (p === '/newproduct') return 'product-new';
    if (p.startsWith('/newuserproduct')) return 'user-list';
    if (p.startsWith('/product-sets')) return 'product-sets';
    if (p.startsWith('/product')) return 'product-list';
    if (p.startsWith('/withdrawal')) return 'withdrawal';
    if (p.startsWith('/orders')) return 'orders';
    if (p.startsWith('/settings')) return 'settings';
    if (p.startsWith('/administrator')) return 'administrator';
    if (p.startsWith('/lucky-spin')) return 'lucky-spin';
    if (p.startsWith('/user')) return 'user-list';
    return '';
}

/** The group containing a given leaf, so it can auto-expand. */
export function groupKeyForLeaf(leafKey: string): string | undefined {
    for (const section of navSections) {
        for (const item of section.items) {
            if (isGroup(item) && item.children.some((c) => c.key === leafKey)) {
                return item.key;
            }
        }
    }
    return undefined;
}
